import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteContent } from '../data/mock';
import { MapPin, Clock, Sparkles, Navigation } from 'lucide-react';

const AshramSection = () => {
  const { language } = useLanguage();
  const content = siteContent[language].ashram;

  return (
    <section id="ashram" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-orange-800 mb-4">
            {content.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-orange-600 max-w-2xl mx-auto">
            {content.subtitle}
          </p>
          <div className="mt-6 flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          {/* Info Section */}
          <div className="space-y-6 md:space-y-8">
            {/* Description */}
            <div className="p-5 md:p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100">
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg">
                {content.description}
              </p>
            </div>

            {/* Address */}
            <div className="p-5 md:p-6 bg-white rounded-2xl shadow-lg border border-orange-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-400 to-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-bold text-orange-800 mb-2">
                    {language === 'gu' ? 'સરનામું' : 'Address'}
                  </h3>
                  <p className="text-gray-700 text-sm md:text-base">{content.address}</p>
                  <a
                    href="https://maps.google.com/?q=MG6R+W23,+Navapara,+Saduthala,+Gujarat+384315,+India"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-orange-600 hover:text-orange-700 font-medium text-sm md:text-base"
                  >
                    <Navigation className="w-4 h-4" />
                    {language === 'gu' ? 'Google Maps પર જુઓ' : 'View on Google Maps'}
                  </a>
                </div>
              </div>
            </div>

            {/* Timings */}
            <div className="p-5 md:p-6 bg-white rounded-2xl shadow-lg border border-orange-100">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-400 to-amber-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-bold text-orange-800 mb-3">
                    {content.timings.title}
                  </h3>
                  <div className="space-y-2">
                    <p className="text-gray-700 flex items-center gap-2 text-sm md:text-base">
                      <span className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0" />
                      {content.timings.morning}
                    </p>
                    <p className="text-gray-700 flex items-center gap-2 text-sm md:text-base">
                      <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0" />
                      {content.timings.evening}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Importance */}
            <div className="p-5 md:p-6 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl border border-orange-200">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-bold text-orange-800 mb-2">
                    {content.importance.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                    {content.importance.content}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Map Section */}
          <div className="relative">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-orange-100">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3654.5!2d72.1!3d23.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDMwJzAwLjAiTiA3MsKwMDYnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Saduthala Ashram Location"
                  className="w-full md:h-[450px]"
                />
              </div>
              <div className="mt-4 text-center">
                <a
                  href="https://maps.google.com/?q=MG6R+W23,+Navapara,+Saduthala,+Gujarat+384315,+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 md:px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-lg hover:shadow-xl text-sm md:text-base"
                >
                  <Navigation className="w-4 h-4 md:w-5 md:h-5" />
                  {language === 'gu' ? 'દિશાનિર્દેશ મેળવો' : 'Get Directions'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AshramSection;
